// src/components/Header/NotificationsPopover.jsx
import React from 'react';
import {
  Box,
  Typography,
  Divider,
  Popover,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import PropTypes from 'prop-types';
import EmptyState from '../common/EmptyState';

const NotificationsPopover = ({
  anchorEl,
  isOpen,
  onClose,
  notifications,
  loadingNotifications,
}) => {
  const theme = useTheme();

  // Map the notification type to an icon
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'info':
        return <InfoIcon color="info" fontSize="small" />;
      case 'warning':
        return <WarningIcon color="warning" fontSize="small" />;
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      default:
        return <InfoIcon color="info" fontSize="small" />;
    }
  };

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={Fade}
      PaperProps={{
        sx: { width: 350, maxHeight: 450, mt: 1.5, borderRadius: 2 },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: theme.palette.primary.main }}
        >
          Notifications
        </Typography>
      </Box>
      <Divider />
      {loadingNotifications ? (
        <EmptyState
          title="Loading notifications"
          description="Please wait while we fetch the latest updates."
          sx={{ py: 2.2 }}
        />
      ) : notifications.length > 0 ? (
        <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
          {notifications.map((notification, index) => (
            <ListItem key={index} alignItems="flex-start" sx={{ py: 1.5, px: 2 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {getNotificationIcon(notification.type)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {notification.title || 'Notification'}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="textSecondary">
                    {notification.message} —{' '}
                    {new Date(notification.date).toLocaleString()}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <EmptyState
          title="No notifications"
          description="You are all caught up. New updates will appear here."
          sx={{ py: 2.2 }}
        />
      )}
    </Popover>
  );
};

NotificationsPopover.propTypes = {
  anchorEl: PropTypes.any,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notifications: PropTypes.array.isRequired,
  loadingNotifications: PropTypes.bool.isRequired,
};

export default NotificationsPopover;
